"use client";

import { Hand, Leaf, Waves, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  icon: Leaf,
  number: "01 · EINFAMILIENHÄUSER",
  title: "Perfekt für Ihr Zuhause",
  description: "Ideal für Einfamilienhäuser und Reihenhäuser, bei denen das oberste Geschoss effizient klimatisiert werden soll – diskret und leistungsstark."
}, {
  icon: Wind,
  number: "02 · DACHGESCHOSS",
  title: "Wohnkomfort unter dem Dach",
  description: "Speziell für Dachgeschosswohnungen entwickelt, um auch an heißen Sommertagen ein angenehmes und kühles Raumklima zu gewährleisten."
}, {
  icon: Hand,
  number: "03 · RAUMHÖHE",
  title: "Für hohe Räume",
  description: "Unsere Systeme sind optimal auf Räume mit hohen Decken abgestimmt, um eine gleichmäßige Luftverteilung ohne störende Zugluft zu erreichen."
}, {
  icon: Waves,
  number: "04 · KLIMATISIERUNG",
  title: "Ganzheitliche Lösung",
  description: "Wir planen und installieren Systeme, die nicht nur kühlen, sondern auch hocheffizient heizen – für Ihr Wohlbefinden das ganze Jahr über."
}];
function PrincipleColumn({
  principle,
  index
}: {
  principle: (typeof principles)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = principle.icon;
  return <div ref={ref} data-index={index} className="relative px-6 py-10 md:px-8 md:py-0 transition-all duration-700 ease-out" style={{
    transitionDelay: `${index * 120}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }}>
      <Icon className="size-12 mb-8 stroke-[1] text-secondary" aria-hidden="true" />
      <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-5 font-default">
        {principle.number}
      </div>
      <h3 className="font-default font-semibold text-[26px] leading-tight text-foreground mb-5">
        {principle.title}
      </h3>
      <p className="text-[15px] leading-relaxed text-muted-foreground font-default">
        {principle.description}
      </p>
    </div>;
}
export default function Applications() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2744" id="einsatzgebiete-für-ihr-zuhause" className="bg-background text-foreground py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <div className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-8 font-default">
            ANWENDUNGSBEREICHE · 04
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground">
            Optimale{" "}
            <span className="italic text-foreground/90">
              Klimatisierung
            </span>
            .
            <br className="hidden sm:block" /> Für{" "}
            <span className="italic text-foreground/90">jedes Zuhause</span>.
          </h2>
        </div>

        <div className="bg-muted/40 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border/60">
            {principles.map((principle, i) => <div key={principle.title} data-index={i} className={i >= 2 ? "sm:border-t sm:border-border/60 lg:border-t-0" : ""}>
                <PrincipleColumn principle={principle} index={i} />
              </div>)}
          </div>
        </div>
      </div>

      <div ref={imageRef} className="mt-20 md:mt-28 lg:mt-32 transition-all duration-1000 ease-out" style={{
      opacity: imageVisible ? 1 : 0,
      transform: imageVisible ? "translateY(0)" : "translateY(32px)"
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714949/img/ducted_ac_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714949/img/ducted_ac_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714949/img/ducted_ac_installation.png-wvc-sizes" alt="Ducted AC System im Dachbodenbereich" loading="lazy" decoding="async" className="w-full h-[50vh] md:h-[65vh] lg:h-[75vh] object-cover" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6">
          <p className="font-default italic text-sm md:text-base text-muted-foreground font-default">
            Ducted AC System Technologie, die für ein perfektes Raumklima in Ihrem gesamten Haus sorgt.
          </p>
        </div>
      </div>
    </section>;
}